import { Alert } from '@apollo/orbit';
import React, { useMemo } from 'react';
import { useLocation } from 'react-router';
import { assertUnreachableOrReturnDefault } from 'register-apollo-graphql-language/src/utils/assertUnreachable';

import { HeaderTab } from 'src/components/common/headerTab/HeaderTab';
import { PageLayout } from 'src/components/common/pageLayout/PageLayout';
import { PageTitle } from 'src/components/common/pageTitle/PageTitle';
import { URLModal } from 'src/components/common/URLModal';
import { PermissionBlockCard } from 'src/components/permissionGuards/permissionBlockCard/PermissionBlockCard';
import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { useTrackL1PageView } from 'src/lib/analytics/amplitude/hooks/useTrackL1PageView';
import Config from 'src/lib/config';

import { CustomChecksConfigPage } from '../checksPage/customChecksConfigPage/CustomChecksConfigPage';
import { LinterConfigPage } from '../checksPage/linterConfigPage/LinterConfigPage';
import { ProposalChecksConfigVariantPage } from '../checksPage/proposalChecksConfigVariantPage/ProposalChecksConfigVariantPage';
import { VariantChecksConfigPage } from '../checksPage/variantChecksConfigPage/VariantChecksConfigPage';
import {
  graphSettingsRouteConfig,
  variantSettingsRouteConfig,
} from '../routes';

import { VariantContractsSettingsPage } from './VariantContractsSettingsPage';
import { VariantExplorerSettingsPage } from './VariantExplorerSettingsPage';
import { VariantGeneralSettingsPage } from './VariantGeneralSettingsPage';
import { VariantRoutingSettingsPage } from './VariantRoutingSettingsPage';
import { ContractConfigWizard } from './variantSettingsCards/contractConfigWizard/ContractConfigWizard';
import { VariantSettingsMenu } from './VariantSettingsMenu';

export const VariantSettingsLayout = ({
  isCloudSupergraph,
}: {
  isCloudSupergraph: boolean;
}) => {
  const location = useLocation();
  const {
    tab = 'general',
    graphId,
    graphVariant,
  } = useRouteParams(variantSettingsRouteConfig);
  const graphRef = useMemo(
    () => ({ graphId, graphVariant }),
    [graphId, graphVariant],
  );

  const [variantSettingsL3Width, setVariantSettingsL3Width] = useLocalStorage(
    'variantSettingsL3Width',
  );

  useTrackL1PageView('settingsL1', { graphRef });

  return (
    <PageLayout
      title={
        <PageTitle
          title="Settings"
          tabs={
            <>
              <HeaderTab
                tabID={`This Variant (${graphVariant})`}
                isActive={() => true}
                to={location}
              />
              <HeaderTab
                tabID="This Graph"
                to={graphSettingsRouteConfig.locationFrom({
                  location,
                  fromRouteConfig: variantSettingsRouteConfig,
                })}
                isActive={() => false}
              />
            </>
          }
        />
      }
      l3MenuProps={{
        minWidth: 140,
        maxWidth: 600,
        setWidth: setVariantSettingsL3Width,
        width: variantSettingsL3Width,
      }}
      l3Menu={
        <VariantSettingsMenu
          graphRef={graphRef}
          isCloudSupergraph={isCloudSupergraph}
        />
      }
    >
      <div className="w-full space-y-4 py-4 pl-1 pr-4">
        {tab === 'general' ? (
          <PermissionGuard
            graphId={graphId}
            graphPermissions="canQueryStats"
            fallback={PermissionBlockCard}
          >
            <VariantGeneralSettingsPage graphRef={graphRef} />
          </PermissionGuard>
        ) : tab === 'explorer' ? (
          <PermissionGuard
            graphId={graphId}
            graphVariant={graphVariant}
            graphVariantPermissions="canManageExplorerSettings"
            fallback={PermissionBlockCard}
          >
            <VariantExplorerSettingsPage />
          </PermissionGuard>
        ) : tab === 'contracts' ? (
          <PermissionGuard
            graphId={graphId}
            graphPermissions="canManageVariants"
            fallback={PermissionBlockCard}
          >
            <VariantContractsSettingsPage />
          </PermissionGuard>
        ) : tab === 'routing' ? (
          isCloudSupergraph && <VariantRoutingSettingsPage />
        ) : tab === 'checks_operations' || tab === 'checks' ? (
          <PermissionGuard
            graphId={graphId}
            graphPermissions="canQueryCheckConfiguration"
            fallback={PermissionBlockCard}
          >
            <VariantChecksConfigPage graphRef={graphRef} />
          </PermissionGuard>
        ) : tab === 'checks_custom_checks' ? (
          <PermissionGuard
            graphId={graphId}
            graphPermissions="canQueryCheckConfiguration"
            fallback={PermissionBlockCard}
          >
            <CustomChecksConfigPage graphRef={graphRef} type="variant" />
          </PermissionGuard>
        ) : tab === 'checks_linter' ? (
          <PermissionGuard
            graphId={graphId}
            graphPermissions="canQueryCheckConfiguration"
            fallback={PermissionBlockCard}
          >
            <LinterConfigPage graphRef={graphRef} type="variant" />
          </PermissionGuard>
        ) : tab === 'checks_proposals' ? (
          <PermissionGuard
            graphId={graphId}
            graphPermissions="canQueryCheckConfiguration"
            fallback={PermissionBlockCard}
          >
            <Alert status="info">
              We recommend turning on proposal checks only for the variant that
              most closely resembles the state of your main git branch.
            </Alert>
            <ProposalChecksConfigVariantPage graphRef={graphRef} />
          </PermissionGuard>
        ) : (
          assertUnreachableOrReturnDefault(tab, null)
        )}
      </div>

      <URLModal name={Config.modals.configureContractWizard}>
        {({ isOpen, closeModal }) => (
          <ContractConfigWizard isOpen={isOpen} onClose={closeModal} />
        )}
      </URLModal>
    </PageLayout>
  );
};
